import {
  ChevronLeft as ChevronLeftIcon,
  Menu as MenuIcon
} from '@mui/icons-material';
import { AppBar, Box, Collapse, IconButton, alpha } from '@mui/material';
import React, { useCallback, useMemo, useState } from 'react';
import { DndProvider, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useNavigate } from 'react-router-dom';

import { useCanvasStore } from '../stores/canvasStore.js';
import { Canvas } from './Canvas.js';
import { WidgetPalette } from './WidgetPalette.js';

interface DroppedWidget {
  type: string;
  config: any;
  size: { width: number; height: number };
}

type CanvasDropZoneProps = {
  readOnly?: boolean;
};

const CanvasDropZone: React.FC<CanvasDropZoneProps> = ({ readOnly }) => {
  const activeCanvas = useCanvasStore((state) => state.getActiveCanvas());
  const addWidget = useCanvasStore((state) => state.addWidget);

  const accept = React.useMemo(() => (readOnly ? [] : ['widget']), [readOnly]);

  const [{ isOver }, drop] = useDrop<DroppedWidget, unknown, { isOver: boolean }>({
    accept,
    drop: (item, monitor) => {
      if (readOnly || !activeCanvas) return;

      const clientOffset = monitor.getClientOffset();
      if (!clientOffset) return;

      // Get the canvas element position
      const canvasElement = document.querySelector('.react-flow');
      if (!canvasElement) return;

      const canvasRect = canvasElement.getBoundingClientRect();
      
      // Calculate position relative to canvas
      const position = {
        x: clientOffset.x - canvasRect.left - (item.size.width / 2),
        y: clientOffset.y - canvasRect.top - (item.size.height / 2),
      };

      // Add widget at drop position
      addWidget(item.type, position, item.config);
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  });

  return (
    <Box
      ref={drop}
      sx={{
        flex: 1,
        position: 'relative',
        backgroundColor: isOver ? 'action.hover' : 'background.default',
        transition: 'background-color 0.2s',
      }}
    >
      <Canvas />
    </Box>
  );
};

type CanvasLayoutProps = {
  hidePalette?: boolean;
  showHeaderActions?: boolean; // new/save/load
  rightHeaderContent?: React.ReactNode;
  paletteInitiallyOpen?: boolean;
  readOnly?: boolean;
};

export const CanvasLayout: React.FC<CanvasLayoutProps> = ({
  hidePalette = false,
  showHeaderActions = false,
  rightHeaderContent,
  paletteInitiallyOpen = true,
  readOnly = false,
}) => {
  const activeCanvas = useCanvasStore((state) => state.getActiveCanvas());
  // const canvases = useCanvasStore((state) => state.canvases); // TODO: Use for canvas switcher
  const createCanvas = useCanvasStore((state) => state.createCanvas);
  const saveLayout = useCanvasStore((state) => state.saveLayout);
  const clearCanvas = useCanvasStore((state) => state.clearCanvas);
  const navigate = useNavigate();
  const paletteAvailable = !hidePalette && !readOnly;
  const [paletteOpen, setPaletteOpen] = useState(paletteAvailable && paletteInitiallyOpen);

  const togglePalette = useCallback(() => {
    if (!paletteAvailable) return;
    setPaletteOpen((prev) => !prev);
  }, [paletteAvailable]);

  const paletteToggleIcon = useMemo(
    () => (paletteOpen ? <ChevronLeftIcon fontSize="small" /> : <MenuIcon fontSize="small" />),
    [paletteOpen]
  );

  const handleNewCanvas = useCallback(() => {
    const name = prompt('Enter canvas name:');
    if (name) {
      createCanvas(name);
    }
  }, [createCanvas]);

  const handleSaveLayout = useCallback(() => {
    if (!activeCanvas || readOnly) return;

    const name = prompt('Enter layout name:');
    if (name) {
      saveLayout({
        name,
        description: '',
        canvasState: activeCanvas,
      });
    }
  }, [activeCanvas, readOnly, saveLayout]);

  const handleClearCanvas = useCallback(() => {
    if (!activeCanvas || readOnly) return;

    if (confirm('Are you sure you want to clear all widgets from the canvas?')) {
      clearCanvas(activeCanvas.id);
    }
  }, [activeCanvas, clearCanvas, readOnly]);

  return (
    <DndProvider backend={HTML5Backend}>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <AppBar position="static" variant="outlined" elevation={0}>
          
        </AppBar>

        <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          <Box sx={{ display: 'flex', flex: 1, position: 'relative', overflow: 'hidden' }}>
            {paletteAvailable && (
              <>
                <Collapse
                  orientation="horizontal"
                  in={paletteOpen}
                  collapsedSize={0}
                  sx={{
                    display: 'flex',
                    height: '100%',
                    '& .MuiCollapse-wrapperInner': { display: 'flex' },
                  }}
                >
                  <WidgetPalette />
                </Collapse>
                <IconButton
                  size="small"
                  onClick={togglePalette}
                  aria-label={paletteOpen ? 'Close widget palette' : 'Open widget palette'}
                  sx={{
                    position: 'absolute',
                    top: 16,
                    left: paletteOpen ? 300 : 16,
                    zIndex: 10,
                    transition: 'left 0.3s ease',
                    backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.85),
                    boxShadow: (theme) => theme.shadows[4],
                    '&:hover': {
                      backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.95),
                    },
                  }}
                >
                  {paletteToggleIcon}
                </IconButton>
              </>
            )}
            <CanvasDropZone readOnly={readOnly} />
          </Box>
        </Box>
      </Box>
    </DndProvider>
  );
};
